import moment from "moment";
import sessionHandling from "services/utils/notificationUtils";
import { apiErrorText } from "pages/PayRoll/PayInputs/ShiftEarnings/shiftEarningsApi";
import { collectComponentRows, fromComponentApi, normalizeCategory, uniqueComponentRows } from "../SalaryComponents/salaryComponentApi";
import { baseForApi, joinBase } from "../SalaryComponents/salaryComponentData";

// Payroll > Salary > Salary Templates on the payroll/salary/ APIs (spec
// section 5: a template change reaches employees only through a
// Template-Based revision).
//
// The API doc has no sample responses, so every reader below accepts the
// likely field names and falls back to safe defaults. Check them against a
// live response.

const BASE = "payroll/salary/";
const TEMPLATE = `${BASE}salary-template/`;
export const query = (path, params) => {
  const search = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ""),
  ).toString();
  return search ? `${path}?${search}` : path;
};

// The salary APIs answer an action the current state doesn't allow (a used
// template, an approved request, ...) with 400 and a message, so 409 is only
// the signed-in-elsewhere logout (same as the Salary Components helper).
export const request = async (domain, path, { method = "GET", body, signal } = {}) => {
  const response = await fetch(`${domain.replace(/\/$/, "")}/${path}`, {
    method,
    signal,
    headers: { "Content-Type": "application/json", Authorization: `Token ${localStorage.getItem("token")}` },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (response.status === 401) {
    localStorage.clear();
    window.location.href = "/auth/login";
    throw new Error("Your session has expired. Please sign in again.");
  }
  if (response.status === 204 && response.ok) return null;
  const data = await response.json().catch(() => null);
  const message = apiErrorText(data?.error || data?.detail || data?.message) || (response.ok ? "" : apiErrorText(data));
  if (response.status === 409) sessionHandling();
  if (!response.ok) throw new Error(message || `Request failed (${response.status}). Please try again.`);
  return data;
};

// ---------- Readers (shared with Assign Salary) ----------

export const unwrap = (data) => (data && !Array.isArray(data) && data.data !== undefined ? data.data : data);
export const listOf = (data, keys = []) => {
  const value = unwrap(data);
  if (Array.isArray(value)) return value;
  for (const key of ["results", "rows", "items", ...keys]) {
    if (Array.isArray(value?.[key])) return value[key];
  }
  return null;
};
export const first = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");
// The success text the server sends with a save ("Standard created as V1.").
const messageOf = (data) => (typeof data?.message === "string" ? data.message.trim() : "");
export const toNumber = (value) => {
  const number = Number(value);
  return value === null || value === undefined || value === "" || !Number.isFinite(number) ? null : number;
};
export const toBool = (value, fallback) => {
  if (typeof value === "boolean") return value;
  if (value === "true" || value === 1 || value === "1" || value === "Yes") return true;
  if (value === "false" || value === 0 || value === "0" || value === "No") return false;
  return fallback;
};
export const toDay = (value) => {
  if (!value) return "";
  const parsed = moment(value, ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", moment.ISO_8601]);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};
export const personName = (value) =>
  typeof value === "object" && value ? first(value.name, value.user_name, value.full_name, value.username) || "" : value || "";
export const versionNumber = (value) => toNumber(String(value ?? "").replace(/^v/i, ""));
export const label = (item) =>
  typeof item === "string" ? item : first(item?.label, item?.value, item?.name, item?.code, item?.key);

// A row's calculation in the screen's shape (amount / percentage / base /
// formula), or null when the object carries none.
const toCalc = (row) => {
  if (!row || typeof row !== "object") return null;
  const method = first(row.method, row.computation_method, row.calculation_method);
  if (!method) return null;
  const value = first(row.value, row.computation_value, row.amount, row.percentage);
  return {
    method,
    amount: method === "Fixed Amount" ? toNumber(value) ?? "" : "",
    percentage: method === "Percentage" ? toNumber(value) ?? "" : "",
    base: method === "Percentage" ? joinBase(first(row.percent_base_code, row.base_code, row.value_based_on, row.base)) || "GROSS" : "GROSS",
    formula: method === "Formula" ? first(row.formula) || "" : "",
  };
};

// Template row (earning / deduction line) -> the line shape the screen and
// the shared helpers use. `default` is the pinned component version's own
// calculation, so an override can be shown and reset.
export const fromTemplateRowApi = (row) => {
  const calc = toCalc(row) || { method: "Fixed Amount", amount: "", percentage: "", base: "GROSS", formula: "" };
  const defaultCalc =
    toCalc(first(row.default, row.component_default, row.default_calculation, row.component_calculation)) ||
    toCalc({
      method: row.default_method,
      value: row.default_value,
      percent_base_code: row.default_percent_base_code,
      formula: row.default_formula,
    });
  const overridden = toBool(first(row.is_overridden, row.overridden), undefined);
  return {
    component_id: first(row.component_id, row.component?.id, row.id),
    code: first(row.code, row.component_code, row.component?.code) || "",
    name: first(row.name, row.component_name, row.component?.name) || "",
    category: normalizeCategory(first(row.category, row.component_category, row.component?.category)),
    component_version: versionNumber(first(row.component_version_no, row.component_version, row.pinned_version, row.version)) ?? 1,
    latest_version: versionNumber(first(row.latest_version_no, row.latest_version, row.component_latest_version)),
    upgrade_available: toBool(row.upgrade_available, false),
    ...calc,
    prorate: toBool(row.prorate, true),
    balancing: toBool(first(row.is_balancing, row.balancing), false),
    // Without the default from the server, a row the server marks as not
    // overridden is its own default.
    default: defaultCalc || (overridden === false || overridden === undefined ? { ...calc } : null),
    overridden: overridden ?? null,
    server_amount: toNumber(first(row.amount_at_gross, row.computed_amount, row.calculated_amount, row.preview_amount)),
  };
};

const SPLIT = (rows) => rows.filter(Boolean);

// The per-category row lists an answer may carry instead of one list.
const GROUP_KEYS = ["earnings", "other_earnings", "deductions", "other_deductions", "employer_contributions", "reimbursements", "calculations"];
const groupedRows = (value) => GROUP_KEYS.flatMap((key) => (Array.isArray(value?.[key]) ? value[key] : []));

// List row / single -> the template object the screen uses. versions holds
// the current version only; lines come with the single GET.
export const fromTemplateApi = (row) => {
  const status = first(row.status, row.usage_status) || "";
  const active = toBool(first(row.is_active, row.active), undefined) ?? status !== "Inactive";
  const version = versionNumber(first(row.current_version, row.version, row.version_number)) ?? 1;
  const rowsList = Array.isArray(row.rows)
    ? row.rows
    : Array.isArray(row.components)
      ? row.components
      : groupedRows(row);
  return {
    id: first(row.id, row.template_id),
    name: first(row.name, row.template_name) || "",
    description: row.description || "",
    pay_basis: first(row.pay_basis, row.pay_frequency) || "Monthly",
    active,
    status,
    employee_count: toNumber(first(row.employee_count, row.employees_count, row.used_by_employees, row.employees)),
    components_count: toNumber(first(row.components_count, row.component_count)),
    earnings_count: toNumber(first(row.earnings_count, row.earning_count)),
    deductions_count: toNumber(first(row.deductions_count, row.deduction_count)),
    updates_count: toNumber(first(row.updates_count, row.upgrades_available, row.component_updates)),
    // The gross the template was saved at (salary_template.salary).
    preview_gross: toNumber(first(row.salary, row.preview_gross, row.default_gross, row.median_gross)),
    deactivation: active
      ? null
      : {
          at: toDay(first(row.deactivated_at, row.deactivated_on, row.updated_at)),
          by: personName(first(row.deactivated_by, row.updated_by)),
          reason: first(row.deactivation_reason, row.inactive_reason, row.reason) || "",
        },
    versions: [
      {
        version,
        version_id: first(row.version_id, row.current_version_id),
        created_at: first(row.version_created_at, row.updated_at, row.created_at) || "",
        created_by: personName(first(row.version_created_by, row.updated_by, row.created_by)),
        note: first(row.change_note, row.note) || "",
        lines: SPLIT(rowsList).map(fromTemplateRowApi),
      },
    ],
    employees: [],
    activity: [],
  };
};

// Preview answer (single GET, template-preview, preview-salary-structure) ->
// { values: code -> amount, the totals, errors } or null. The single GET
// keeps the rows next to its preview object, not in it.
export const fromPreviewApi = (data, lines = []) => {
  const value = unwrap(data);
  if (!value || typeof value !== "object") return null;
  const preview = value.preview && typeof value.preview === "object" ? value.preview : value;
  const rows =
    listOf(preview, ["components", "lines", "earnings_rows"]) ||
    (Array.isArray(value.components) ? value.components : null) ||
    groupedRows(preview);
  const codeById = Object.fromEntries(lines.map((line) => [String(line.component_id), line.code]));
  const values = {};
  rows.forEach((row) => {
    const code = first(row.code, row.component_code, codeById[String(first(row.component_id, row.id))]);
    const amount = toNumber(first(row.amount, row.value, row.computed_amount, row.amount_at_gross));
    if (code && amount != null) values[code] = amount;
  });
  const totals = preview.totals && typeof preview.totals === "object" ? preview.totals : preview;
  const errorsRaw = first(preview.errors, value.errors, preview.validation_errors) || [];
  const errors = (Array.isArray(errorsRaw) ? errorsRaw : [errorsRaw])
    .map((item) => (typeof item === "string" ? item : apiErrorText(first(item?.message, item?.error, item))))
    .filter(Boolean);
  const earnings = toNumber(first(totals.earnings_total, totals.total_earnings, totals.earnings, totals.gross_total));
  const deductions = toNumber(first(totals.deductions_total, totals.total_deductions, totals.deductions));
  const net = toNumber(first(totals.net, totals.net_pay, totals.net_before_statutory));
  if (!Object.keys(values).length && earnings == null && !errors.length) return null;
  return {
    gross: toNumber(first(preview.gross, totals.gross)),
    values,
    earnings,
    deductions,
    net,
    otherEarnings: toNumber(totals.other_earnings),
    otherDeductions: toNumber(totals.other_deductions),
    employer: toNumber(totals.employer_contribution),
    reimbursement: toNumber(totals.reimbursement),
    ctc: toNumber(totals.ctc),
    errors,
  };
};

const fromEmployeeApi = (row, index) => ({
  id: String(first(row.id, row.salary_id, `${row.employee_id}-${index}`)),
  name: first(row.employee_name, row.user_name, row.name) || "",
  employee_code: String(first(row.employee_code, row.employee_id, row.emp_code) ?? ""),
  department: first(row.department, row.department_name) || "",
  version: versionNumber(first(row.template_version, row.version)) ?? 1,
  customized: toBool(first(row.is_customized, row.customized), String(first(row.structure_type, row.structure) || "").toLowerCase() === "customized"),
  gross: toNumber(first(row.gross, row.gross_salary)),
  effective_from: toDay(first(row.effective_from, row.effective_date)),
});

const fromVersionApi = (row) => ({
  version: versionNumber(first(row.version, row.version_number, row.version_no)) ?? 1,
  version_id: first(row.version_id, row.id),
  created_at: first(row.created_on, row.created_at) || "",
  created_by: personName(first(row.created_by_name, row.created_by)),
  note: first(row.change_note, row.note) || "",
  employee_count: toNumber(first(row.employees, row.employee_count, row.employees_count)),
  components_count: toNumber(first(row.components_count, row.component_count)),
  lines: Array.isArray(row.rows) ? row.rows.map(fromTemplateRowApi) : [],
});

// ---------- List / details ----------

// A template-like row: an id plus a name.
const isTemplateRow = (row) =>
  row && typeof row === "object" && !Array.isArray(row) &&
  first(row.id, row.template_id) != null && first(row.name, row.template_name) != null;

// Template rows wherever the answer keeps them (a plain list, or under
// data / results / templates / list next to the summary card counts).
const findTemplateRows = (value, depth = 0) => {
  if (depth > 3 || value == null || typeof value !== "object") return [];
  if (Array.isArray(value)) {
    return value.some(isTemplateRow) ? value.filter(isTemplateRow) : value.flatMap((item) => findTemplateRows(item, depth + 1));
  }
  return Object.values(value).flatMap((item) => findTemplateRows(item, depth + 1));
};

// The list. An answer with no template rows is an empty company; only a
// non-JSON / non-object answer is an error.
export const listSalaryTemplates = async (domain, companyId, signal) => {
  const data = await request(domain, query(TEMPLATE, { company_id: companyId }), { signal });
  if (!data || typeof data !== "object") throw new Error("The server returned an invalid salary template list.");
  const list = listOf(data, ["templates", "salary_templates"]) || findTemplateRows(unwrap(data));
  const seen = new Set();
  return list
    .filter((row) => {
      const key = String(first(row.id, row.template_id));
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map(fromTemplateApi);
};

// One template with its rows and its preview. Only template_id is sent:
// the server works out the gross itself.
export const getSalaryTemplate = async (domain, templateId, signal) => {
  const data = unwrap(await request(domain, query(TEMPLATE, { template_id: templateId }), { signal }));
  const row = Array.isArray(data) ? data[0] : data?.template || data;
  if (!row || typeof row !== "object") throw new Error("The server returned an invalid salary template.");
  const template = fromTemplateApi(row);
  return { template, preview: fromPreviewApi(data, template.versions[0].lines) };
};

// Everything the view shows: the template + rows + preview, the Employees
// tab and the versions for the Activity Log. A tab whose call fails stays
// empty and its error is returned.
export const templateDetails = async (domain, listTemplate, signal) => {
  const id = listTemplate.id;
  const [single, employees, versions] = await Promise.allSettled([
    getSalaryTemplate(domain, id, signal),
    request(domain, query(`${BASE}template-employees/`, { template_id: id }), { signal }),
    request(domain, query(`${BASE}template-versions/`, { template_id: id }), { signal }),
  ]);
  const errors = [single, employees, versions]
    .filter((result) => result.status === "rejected" && result.reason?.name !== "AbortError")
    .map((result) => result.reason?.message);
  const base = single.status === "fulfilled" ? { ...listTemplate, ...single.value.template } : listTemplate;
  const employeeRows = employees.status === "fulfilled" ? listOf(employees.value, ["employees"]) : null;
  const versionRows = versions.status === "fulfilled" ? listOf(versions.value, ["versions"]) : null;
  const current = base.versions[base.versions.length - 1];
  const history = versionRows?.length ? versionRows.map(fromVersionApi).sort((a, b) => a.version - b.version) : [current];
  // The current version keeps its rows from the single GET.
  const last = history[history.length - 1];
  if (last && last.version === current.version) {
    history[history.length - 1] = {
      ...current,
      ...Object.fromEntries(Object.entries(last).filter(([key, value]) => value !== "" && value != null && !(key === "lines" && !value.length))),
      lines: current.lines,
    };
  } else {
    history.push(current);
  }
  return {
    template: {
      ...base,
      versions: history,
      employees: employeeRows ? employeeRows.map(fromEmployeeApi) : [],
      details_loaded: true,
    },
    preview: single.status === "fulfilled" ? single.value.preview : null,
    errors,
  };
};

// Preview of a saved template. Only template_id is sent: the server works
// out the gross itself.
export const getTemplatePreview = async (domain, templateId, lines, signal) =>
  fromPreviewApi(await request(domain, query(`${BASE}template-preview/`, { template_id: templateId }), { signal }), lines);

// ---------- Builder ----------

// Components that can go into a template, every template category (see
// TEMPLATE_CATEGORIES), with their current calculation (same shape as
// Salary Components' list). An answer with no rows gives an empty picker.
export const getTemplateComponents = async (domain, companyId, signal) => {
  const data = await request(domain, query(`${BASE}get-template-components/`, { company_id: companyId }), { signal });
  return uniqueComponentRows(collectComponentRows(unwrap(data)))
    .map(fromComponentApi)
    .map((item) => ({ ...item, active: true, system: false }));
};

// Pay basis options, methods and the default preview gross per basis.
export const getTemplateChoices = async (domain, signal) => {
  const data = unwrap(await request(domain, `${BASE}get-salary-template-choices/`, { signal }));
  const labels = (value) => (Array.isArray(value) ? value.map(label).filter(Boolean) : null);
  const grossRaw = first(data?.default_preview_gross, data?.preview_gross, data?.default_gross, data?.preview_gross_by_basis);
  const defaultGross = {};
  if (Array.isArray(grossRaw)) {
    grossRaw.forEach((item) => {
      const basis = first(item?.pay_basis, item?.label, item?.basis);
      const gross = toNumber(first(item?.gross, item?.value, item?.preview_gross));
      if (basis && gross != null) defaultGross[basis] = gross;
    });
  } else if (grossRaw && typeof grossRaw === "object") {
    Object.entries(grossRaw).forEach(([basis, gross]) => {
      if (toNumber(gross) != null) defaultGross[basis] = toNumber(gross);
    });
  }
  (Array.isArray(data?.pay_basis_choices) ? data.pay_basis_choices : []).forEach((item) => {
    const gross = toNumber(first(item?.default_gross, item?.preview_gross, item?.gross));
    if (label(item) && gross != null) defaultGross[label(item)] = gross;
  });
  return {
    payBases: labels(first(data?.pay_basis_choices, data?.pay_basis, data?.pay_bases)),
    methods: labels(first(data?.computation_method_choices, data?.method_choices, data?.methods)),
    defaultGross,
  };
};

// Form lines -> the rows the template APIs take, in payslip order. A row
// sends its calculation only when it differs from the component's default,
// is_balancing on the balancing earning, and upgrade when it was moved to
// the component's latest version.
export const toComponentRows = (lines, isOverridden) =>
  lines.map((line) => {
    // is_balancing goes on every row, so a row that stopped balancing (Basic
    // on older templates) is cleared on the server too.
    const row = { component_id: line.component_id, is_balancing: Boolean(line.balancing) };
    if (!line.balancing && isOverridden(line)) {
      row.method = line.method;
      if (line.method === "Fixed Amount") row.value = Number(line.amount);
      if (line.method === "Percentage") {
        row.value = Number(line.percentage);
        row.percent_base_code = baseForApi(line.base);
      }
      if (line.method === "Formula") row.formula = String(line.formula || "").trim();
    }
    if (line.upgrade) row.upgrade = true;
    return row;
  });

// Live preview / validation of an unsaved structure.
export const previewSalaryStructure = async (domain, companyId, gross, rows, lines, signal) =>
  fromPreviewApi(
    await request(domain, `${BASE}preview-salary-structure/`, {
      method: "POST",
      body: { company_id: Number(companyId), gross: Number(gross), components: rows },
      signal,
    }),
    lines,
  );

// ---------- Save / status ----------

export const createSalaryTemplate = async (domain, companyId, form, rows, previewGross, signal) => {
  const data = unwrap(
    await request(domain, TEMPLATE, {
      method: "POST",
      body: {
        company_id: Number(companyId),
        name: form.name.trim(),
        description: form.description.trim(),
        pay_basis: form.pay_basis,
        salary: Number(previewGross),
        components: rows,
      },
      signal,
    }),
  );
  return { id: first(data?.id, data?.template_id, data?.template?.id) ?? null, message: messageOf(data) };
};

// Unused: edited in place. Used: pay basis locked, row changes save
// V(n+1) with change_note; the answer may carry
// employees_on_previous_versions and a revise_prompt. salary is the gross
// the form shows: the server checks the rows at it and keeps it on the
// template, so the form and the view open at it next time.
export const updateSalaryTemplate = async (domain, id, form, rows, { newVersion, used, previewGross }, signal) => {
  const data = unwrap(
    await request(domain, TEMPLATE, {
      method: "PUT",
      body: {
        template_id: id,
        name: form.name.trim(),
        description: form.description.trim(),
        ...(used ? {} : { pay_basis: form.pay_basis }),
        ...(newVersion ? { change_note: form.note.trim() } : {}),
        ...(Number(previewGross) > 0 ? { salary: Number(previewGross) } : {}),
        components: rows,
      },
      signal,
    }),
  );
  return {
    message: messageOf(data),
    employeesOnPrevious: toNumber(first(data?.employees_on_previous_versions, data?.employees_on_previous_version)),
    revisePrompt: first(data?.revise_prompt?.message, data?.revise_prompt) || "",
  };
};

// Status changes and delete resolve to the server's message for the toast.
export const deactivateSalaryTemplate = async (domain, id, reason, signal) =>
  messageOf(await request(domain, TEMPLATE, { method: "PUT", body: { template_id: id, status: "Inactive", reason }, signal }));

export const activateSalaryTemplate = async (domain, id, signal) =>
  messageOf(await request(domain, TEMPLATE, { method: "PUT", body: { template_id: id, status: "Active" }, signal }));

// Soft delete: Unused templates only (a Used one answers 400).
export const deleteSalaryTemplate = async (domain, id, signal) =>
  messageOf(await request(domain, TEMPLATE, { method: "PUT", body: { template_id: id, is_deleted: true }, signal }));

// Duplicate as a new Unused V1. When the pay basis changes, the fixed-amount
// rows to re-check come back in review_rows.
export const duplicateSalaryTemplate = async (domain, id, name, payBasis, signal) => {
  const data = unwrap(
    await request(domain, `${BASE}template-duplicate/`, {
      method: "POST",
      body: { template_id: id, name: name.trim(), pay_basis: payBasis },
      signal,
    }),
  );
  const reviewRaw = first(data?.review_rows, data?.template?.review_rows) || [];
  return {
    message: messageOf(data),
    id: first(data?.id, data?.template_id, data?.template?.id, data?.new_template_id) ?? null,
    reviewCodes: (Array.isArray(reviewRaw) ? reviewRaw : [])
      .map((row) => (typeof row === "string" ? row : first(row?.code, row?.component_code, row?.component_id)))
      .filter((value) => value != null)
      .map(String),
  };
};

// Name check: null when fine, else the message to show.
export const checkTemplateName = async (domain, companyId, name, templateId, signal) => {
  const fallback = "A template with this name already exists";
  let data;
  try {
    data = unwrap(
      await request(domain, query(`${BASE}check-salary-template-name/`, { company_id: companyId, template_name: name.trim(), template_id: templateId }), { signal }),
    );
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    return error.message || fallback;
  }
  if (typeof data === "boolean") return data ? null : fallback;
  const taken =
    [data?.exists, data?.is_exists, data?.is_exist, data?.is_duplicate, data?.duplicate].some((value) => value === true) ||
    [data?.available, data?.is_available, data?.valid, data?.is_valid, data?.status].some((value) => value === false);
  return taken ? apiErrorText(data?.message || data?.error) || fallback : null;
};
